/**
 * Typovaný HTTP klient.
 *
 * Session je cookie (`stickyiq_session`, viz backend core/auth.py), takže každý
 * request jede s `credentials: 'include'`. V dev to funguje díky Vite proxy —
 * frontend i API jsou pak same-origin.
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    /** česká hláška z backendu, pokud ji poslal */
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isNotFound() {
    return this.status === 404;
  }
}

const BASE = '/api/v1';

/** Handler pro 401 — nastavuje ho App, aby klient nemusel znát router. */
let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: (() => void) | null) {
  onUnauthorized = fn;
}

/**
 * Vytáhne čitelnou hlášku z jedné FastAPI validační chyby.
 *
 * Pydantic posílá `{loc: ['body','password'], msg: 'Value error, Heslo musí…'}`.
 * Uživatele nezajímá `body.password` ani anglická předpona — backend píše
 * vlastní hlášky česky, takže se dostane na povrch jen ta.
 */
export function cleanValidationMessage(item: { msg?: string; loc?: unknown[] }): string {
  const msg = (item.msg ?? '').replace(/^(Value error|Assertion failed),\s*/i, '').trim();
  if (!msg) return '';

  // Pole necháváme jen tam, kde hláška sama neříká, čeho se týká — jinak
  // by z toho bylo „password: Heslo musí mít…“.
  const field = Array.isArray(item.loc)
    ? item.loc.filter((part) => typeof part === 'string' && part !== 'body').join('.')
    : '';

  if (!field || /^[A-ZÁ-Ž]/.test(msg)) return msg;
  return `${field}: ${msg}`;
}

async function parseError(res: Response): Promise<ApiError> {
  let body: unknown;
  let message = `Požadavek selhal (HTTP ${res.status}).`;
  try {
    body = await res.json();
    const detail = (body as { detail?: unknown })?.detail;
    if (typeof detail === 'string') {
      message = detail;
    } else if (Array.isArray(detail)) {
      // FastAPI validační chyby (422)
      const cleaned = detail.map(cleanValidationMessage).filter(Boolean);
      if (cleaned.length > 0) message = cleaned.join(' ');
    }
  } catch {
    /* odpověď nebyla JSON — zůstane obecná hláška */
  }
  return new ApiError(res.status, message, body);
}

export interface RequestOptions {
  signal?: AbortSignal;
  /** query parametry; pole se serializují jako opakovaný klíč */
  params?: Record<string, string | number | boolean | Array<string | number> | null | undefined>;
}

export function buildQuery(
  params: RequestOptions['params'],
): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) sp.append(key, String(v));
    } else {
      sp.set(key, String(value));
    }
  }
  const q = sp.toString();
  return q ? `?${q}` : '';
}

async function request<T>(
  method: string,
  path: string,
  options: RequestOptions & { body?: unknown } = {},
): Promise<T> {
  const { params, body, signal } = options;
  const res = await fetch(`${BASE}${path}${buildQuery(params)}`, {
    method,
    credentials: 'include',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    signal,
  });

  if (res.status === 401) {
    onUnauthorized?.();
    throw await parseError(res);
  }
  if (!res.ok) {
    throw await parseError(res);
  }
  if (res.status === 204) {
    return undefined as T;
  }
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, options),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('POST', path, { ...options, body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PUT', path, { ...options, body }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    request<T>('PATCH', path, { ...options, body }),
  delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, options),
};

/** Absolutní URL pro SSE / stahování (mimo fetch wrapper). */
export function apiUrl(path: string, params?: RequestOptions['params']): string {
  return `${BASE}${path}${buildQuery(params)}`;
}
